import { addDays, startOfWeek } from './formatAppointmentWhen'

export type CalendarView = 'day' | 'week' | 'month'

export const DAY_GRID_START_HOUR = 7
/** Exclusive. The last slot starts at 7:00 PM and the window ends at 8:00 PM. */
export const DAY_GRID_END_HOUR = 20

export function canBookSlot(day: Date, hours: number, minutes: number, now = new Date()): boolean {
  const slot = new Date(day)
  slot.setHours(hours, minutes, 0, 0)
  return slot.getTime() > now.getTime()
}

/** The next grid hour strictly after now. The window is 7:00 AM through 7:00 PM. */
export function nextBookableSlot(now = new Date()): { day: Date; hours: number; minutes: number } {
  const boundary = new Date(now)
  boundary.setMinutes(0, 0, 0)
  if (boundary.getTime() <= now.getTime()) {
    boundary.setHours(boundary.getHours() + 1)
  }
  const day = startOfDay(boundary)
  const hour = boundary.getHours()
  if (hour < DAY_GRID_START_HOUR) {
    return { day, hours: DAY_GRID_START_HOUR, minutes: 0 }
  }
  if (hour >= DAY_GRID_END_HOUR) {
    return { day: addDays(day, 1), hours: DAY_GRID_START_HOUR, minutes: 0 }
  }
  return { day, hours: hour, minutes: 0 }
}

export function dayGridHours(): number[] {
  return Array.from(
    { length: DAY_GRID_END_HOUR - DAY_GRID_START_HOUR },
    (_, index) => DAY_GRID_START_HOUR + index,
  )
}

/**
 * Month cells keep 9:00 AM when that hour is still ahead. After 9:00 AM,
 * today stays bookable while a later grid hour remains, and the click
 * uses that hour.
 */
export function monthBookableSlot(
  day: Date,
  now = new Date(),
): { hours: number; minutes: number } | null {
  if (canBookSlot(day, 9, 0, now)) {
    return { hours: 9, minutes: 0 }
  }

  const hour = dayGridHours().find((candidate) => canBookSlot(day, candidate, 0, now))
  if (hour === undefined) return null
  return { hours: hour, minutes: 0 }
}

export function startOfDay(date: Date): Date {
  const copy = new Date(date)
  copy.setHours(0, 0, 0, 0)
  return copy
}

export function startOfMonth(date: Date): Date {
  const copy = startOfDay(date)
  copy.setDate(1)
  return copy
}

export function addMonths(date: Date, months: number): Date {
  const copy = startOfDay(date)
  const day = copy.getDate()
  copy.setDate(1)
  copy.setMonth(copy.getMonth() + months)
  const lastDay = new Date(copy.getFullYear(), copy.getMonth() + 1, 0).getDate()
  copy.setDate(Math.min(day, lastDay))
  return copy
}

export function dayKey(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

export function isSameMonth(left: Date, right: Date): boolean {
  return left.getFullYear() === right.getFullYear() && left.getMonth() === right.getMonth()
}

export function longDayLabel(date: Date): string {
  return date.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })
}

export function slotTimeLabel(hour: number): string {
  const sample = new Date(2026, 0, 1, hour, 0, 0, 0)
  return sample.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}

export interface VisibleRange {
  start: Date
  end: Date
  days: Date[]
}

export function visibleRange(cursor: Date, view: CalendarView): VisibleRange {
  if (view === 'day') {
    const start = startOfDay(cursor)
    return { start, end: addDays(start, 1), days: [start] }
  }

  if (view === 'week') {
    const start = startOfWeek(cursor)
    const days = Array.from({ length: 7 }, (_, index) => addDays(start, index))
    return { start, end: addDays(start, 7), days }
  }

  const gridStart = startOfWeek(startOfMonth(cursor))
  const days = Array.from({ length: 42 }, (_, index) => addDays(gridStart, index))
  return { start: gridStart, end: addDays(gridStart, 42), days }
}

export function shiftCursor(cursor: Date, view: CalendarView, delta: number): Date {
  if (view === 'day') return addDays(startOfDay(cursor), delta)
  if (view === 'week') return addDays(startOfDay(cursor), delta * 7)
  return addMonths(cursor, delta)
}

export function periodLabel(cursor: Date, view: CalendarView): string {
  if (view === 'day') {
    return cursor.toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })
  }

  if (view === 'week') {
    const start = startOfWeek(cursor)
    const end = addDays(start, 6)
    const startLabel = start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    const endLabel = end.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    return `${startLabel} – ${endLabel}`
  }

  return cursor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
}

export function weekdayLabels(): string[] {
  const monday = startOfWeek(new Date(2026, 0, 5))
  return Array.from({ length: 7 }, (_, index) => (
    addDays(monday, index).toLocaleDateString(undefined, { weekday: 'short' })
  ))
}
